// useGeoLocationEnhanced.tsx
import { useCallback } from 'react';
import { hasLocationPermission } from 'utils/client/hasLocationPermission';
import { useGeoLocationState } from 'context/GeoLocationContext';
import { AUTOCOMPLETE_CONFIG } from 'context/GeoLocationContext/config';

interface Props {
  autocompleteDebounceTime?: number;
  enableAutoInitialization?: boolean;
}

/**
 * Enhanced geolocation hook that provides a simplified interface to the GeoLocationContext
 * This hook is a thin wrapper around the context provider and adds UI-specific convenience methods
 *
 * All business logic has been moved to GeoLocationProvider for better state management.
 * This hook now only handles UI-specific concerns and provides backward compatibility.
 */
export const useGeoLocationEnhanced = (
  {
    autocompleteDebounceTime = AUTOCOMPLETE_CONFIG.DEFAULT_DEBOUNCE,
    enableAutoInitialization = false,
  }: Props = {
    autocompleteDebounceTime: AUTOCOMPLETE_CONFIG.DEFAULT_DEBOUNCE,
    enableAutoInitialization: false,
  },
) => {
  const context = useGeoLocationState();

  // ===== UI-SPECIFIC METHODS (HOOK LEVEL ONLY) =====
  // These methods deal with React events and UI-specific concerns that don't belong in the provider

  /**
   * Form-specific handler for React input events
   * This belongs in the hook because it deals with UI-specific React events
   */
  const handleAutcompleteSuggestions = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      context.setAddressString(value);
      context.setActiveAutocompleteAddress(null);
      context.getAutocompletePlaces(value);
    },
    [context],
  );

  /**
   * Generic text input handler for controlled components
   * Alternative to handleAutcompleteSuggestions when you don't have a React event
   */
  const handleAddressInputChange = useCallback(
    (value: string) => {
      context.setAddressString(value);
      context.setActiveAutocompleteAddress(null);
      context.getAutocompletePlaces(value);
    },
    [context],
  );

  return {
    // ===== ORIGINAL HOOK INTERFACE (EXACT COMPATIBILITY) =====
    // All these methods existed in the original hook and are preserved for backward compatibility

    // Core geolocation methods (exact same names and signatures)
    getExactDetails: context.getExactDetails,
    requestUserLocation: context.requestUserLocationAndUpdateState, // Original name mapping
    getEstimatedLocation: context.getEstimatedLocation,
    getAutocompletePlaces: context.getAutocompletePlaces,
    getAddressFromLatLng: context.getAddressFromLatLng,

    // Original autocomplete methods
    handleSubmitAutocomplete: context.handleSubmitAutocomplete,
    handleAutcompleteSuggestions, // Form handler (hook-level)
    getImmediateAutcompletePlaces: context.getImmediateAutcompletePlaces, // For backward compatibility

    // Original state values (exact same names)
    suggestedAddresses: context.suggestedAddresses,
    activeAutocompleteAddress: context.activeAutocompleteAddress,
    isLoadingAutocomplete: context.isLoadingAutocomplete,
    autocompleteInputLength: context.autocompleteInputLength,
    autocompleteMinLength: AUTOCOMPLETE_CONFIG.MIN_LENGTH,
    isAddressPending: context.isAddressPending,

    // Original state setters
    setSuggestedAddresses: context.setSuggestedAddresses,
    setActiveAutocompleteAddress: context.setActiveAutocompleteAddress,

    // Core state from geolocationState (original interface)
    position: context.position,
    setPosition: context.setPosition,
    isInitializing: context.isInitializing,
    addressString: context.addressString,
    setAddressString: context.setAddressString,
    exactAddress: context.exactAddress,
    setExactAddress: context.setExactAddress,
    errorRequestLocation: context.errorRequestLocation,
    setErrorRequestLocation: context.setErrorRequestLocation,
    isExactCenter: context.isExactCenter,
    setIsExactCenter: context.setIsExactCenter,
    centerLatitude: context.centerLatitude,
    centerLongitude: context.centerLongitude,
    hasSetLocationEstimate: context.hasSetLocationEstimate,
    setHasSetLocationEstimate: context.setHasSetLocationEstimate,
    hasSetExactLocation: context.hasSetExactLocation,
    setHasSetExactLocation: context.setHasSetExactLocation,
    hasPromptedThisSession: context.hasPromptedThisSession,
    setHasPromptedThisSession: context.setHasPromptedThisSession,

    // Manual initialization (from original)
    initializeGeoLocation: context.initializeGeoLocation,

    // Utility function that was re-exported in original
    hasLocationPermission,

    // ===== BUSINESS CONVENIENCE METHODS (NOW FROM PROVIDER) =====
    // These were moved from hook to provider for better state management
    // but are re-exported here for convenience and backward compatibility
    updateAddressAndClearAutocomplete: context.updateAddressAndClearAutocomplete,
    selectAutocompleteSuggestion: context.selectAutocompleteSuggestion,
    clearAutocompleteState: context.clearAutocompleteState,
    resetLocationState: context.resetLocationState,
    shouldShowAutocomplete: context.shouldShowAutocomplete,
    isSearchingAutocomplete: context.isSearchingAutocomplete,

    // ===== UI-SPECIFIC METHODS (HOOK LEVEL ONLY) =====
    // These are the only methods that truly belong at the hook level
    handleAddressInputChange, // Generic input handler for controlled components

    // ===== CONFIGURATION VALUES =====
    // Expose configuration for components that need it
    autocompleteDebounceTime,
    enableAutoInitialization,

    onMapDrag: context.onMapDrag,

    // Google Maps availability
    isGoogleMapsAvailable: context.isGoogleMapsAvailable,
  };
};
